import {
  ChangeSetSummary,
  CloudFormation,
  DescribeChangeSetOutput,
  ListChangeSetsCommandOutput,
} from "@aws-sdk/client-cloudformation";
import CustomDataGrid from "@components/common/DataGrid/CustomDataGrid";
import SearchHeader from "@components/common/SearchHeader";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  GridCellCheckboxRenderer,
  GridColDef,
  GridRowParams,
  GridRowSelectionModel,
  selectedIdsLookupSelector,
} from "@mui/x-data-grid-pro";
import { IStoreState } from "@store/index";
import React, { useState } from "react";
import { useAsync } from "react-async-hook";
import { useSelector } from "react-redux";
import ChangesetDetails from "./ChangesetDetails";

const STAGE = process.env.REACT_APP_STACK;

class ChangeSet {
  constructor(public id: string, public name: string, public status: string) {}
}

const Infrastructure: React.FC = (props) => {
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [rows, setRows] = useState<ChangeSet[]>([]);
  const [changeSetDetails, setChangeSetDetails] = useState<DescribeChangeSetOutput["Changes"] | null>(null);
  const [clickedChangeSetName, setClickedChangeSetName] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [executingStackIds, setExecutingStackIds] = useState<string[] | null>(null);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const credentials = useSelector((store: IStoreState) => store.session.credentials);
  const companies = useSelector((store: IStoreState) => store.companies.companies);

  const cloudformation = new CloudFormation({
    region: process.env.REACT_APP_API_GATEWAY_REGION,
    credentials,
  });

  useAsync(async () => {
    setLoading(true);
    try {
      const infrastructureRows: ChangeSet[] = await fetchChangesets(companies);
      setRows(infrastructureRows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  async function fetchChangesets(reduxCompanies: Company[]) {
    const stackNames = getStackNames(reduxCompanies);

    const changeSetPromises = stackNames.map(async (stackName) => {
      try {
        const changeSetsResponse = await cloudformation.listChangeSets({
          StackName: stackName,
        });
        return getLatestChangeset(changeSetsResponse, stackName);
      } catch (e) {
        console.error(`Error fetching change sets for stack ${stackName}:`, e);
        return null;
      }
    });

    const changeSets = await Promise.all(changeSetPromises);
    return changeSets.filter((changeSet): changeSet is ChangeSet => changeSet !== null);
  }

  function getLatestChangeset(changeSetsResponse: ListChangeSetsCommandOutput, stackName: string): ChangeSet | null {
    const changeSetSummaries = changeSetsResponse.Summaries || [];
    let latestChangeSet: ChangeSetSummary | null = null;

    for (const changeSet of changeSetSummaries) {
      if (!latestChangeSet || (changeSet.CreationTime && changeSet.CreationTime > latestChangeSet.CreationTime!)) {
        latestChangeSet = changeSet;
      }
    }

    return latestChangeSet
      ? new ChangeSet(latestChangeSet.ChangeSetId ?? "", stackName || "", latestChangeSet.Status ?? "Unknown")
      : null;
  }

  function getStackNames(reduxCompanies: Company[]) {
    const toStackName = ({ companyId }: Company): string => `${companyId.replace("_", "-")}-infrastructure-${STAGE}`;

    const stackNames = reduxCompanies.filter(({ companyId }) => companyId !== "forsight").map(toStackName);

    stackNames.push(
      `forsight-dashboard-storage-${STAGE}`,
      `forsight-dashboard-analytics-${STAGE}`,
      `forsight-dashboard-gateway-api-service-${STAGE}`,
      `forsight-dashboard-database-${STAGE}`,
      `forsight-dashboard-email-${STAGE}`,
      `forsight-dashboard-network-${STAGE}`,
      `forsight-aurora-${STAGE}`,
      `dashboard-auth-${STAGE}`,
      `forsight-iot-${STAGE}`,
      `SemanticSegmentationPipelineStack-${STAGE}`,
      `RelationalDatabaseStack-${STAGE}`,
      `Queues-${STAGE}`,
      `MonitoringStack-${STAGE}`,
      `GreengrassV2-${STAGE}`,
      `CameraPositionClassificationPipelineStack-${STAGE}`
    );

    if (STAGE === "staging") {
      stackNames.push(
        "CiCdPipelineStack",
        "CiCdCrossAccountRoleStack",
        "EdgeApplicationStack",
        "EdgeApplicationCiCdCrossAccountRoleStack",
        "WebRTCTunnelManagerCiCdPipelineStack",
        "WebRTCTunnelManagerCiCdCrossAccountRoleStack"
      );
    }
    return stackNames;
  }

  function isExecuteButtonDisabled(): boolean | undefined {
    return (
      !selectedRows.length ||
      selectedRows.some((rowId) => {
        const row = rows.find((r) => r.id === rowId);
        return row?.status !== "CREATE_COMPLETE";
      })
    );
  }

  const changesetColumns = React.useMemo<GridColDef[]>(() => {
    const columns: GridColDef[] = [
      {
        field: "__check__",
        type: "checkboxSelection",
        flex: 0.04,
        resizable: false,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        disableReorder: true,
        disableExport: true,
        valueGetter: ({ api, id }) => {
          const selectionLookup = selectedIdsLookupSelector(api.state);
          return selectionLookup[id] !== undefined;
        },
        renderCell: (params) => <GridCellCheckboxRenderer {...params} />,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.5,
        sortable: true,
      },
      {
        field: "isExecuting",
        headerName: "Executing",
        flex: 0.2,
        renderCell: (params) => {
          return params.value ? <CircularProgress size={24} /> : null;
        },
      },
      {
        field: "changeSetLink",
        headerName: "",
        flex: 0.1,
        renderCell: (params) => {
          const stackArn = params.row.name;
          const region = process.env.REACT_APP_API_GATEWAY_REGION;
          const link = `https://${region}.console.aws.amazon.com/cloudformation/home?region=${region}#/stacks/changesets?stackId=${stackArn}`;
          return (
            <a href={link} target="_blank" rel="noopener noreferrer">
              <OpenInNewIcon style={{ verticalAlign: "middle", marginRight: "4px" }} />
            </a>
          );
        },
      },
    ];
    return columns;
  }, []);

  const fetchChangeSetDetails = async (params: GridRowParams<ChangeSet>) => {
    const changeSet = params.row;
    setClickedChangeSetName(changeSet.name);
    setChangeSetDetails(null);
    setLoadingDetails(true);

    try {
      const response = await cloudformation.describeChangeSet({
        ChangeSetName: changeSet.id,
        StackName: changeSet.name,
      });
      setChangeSetDetails(response.Changes);
      console.log(response);
    } catch (error) {
      console.error("Error fetching change set details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const executeChangesets = async () => {
    setDialogOpen(true);
  };

  const handleConfirmExecution = async () => {
    const selectedChangeSets = rows.filter((row) => selectedRows.includes(row.id));
    setExecutingStackIds(selectedChangeSets.map(({ id }) => id));

    for (const changeSet of selectedChangeSets) {
      try {
        await cloudformation.executeChangeSet({
          ChangeSetName: changeSet.id,
          StackName: changeSet.name,
        });
        console.log(`Executed change set: ${changeSet.id}`);
        checkStackStatus(changeSet.name);
      } catch (error) {
        console.error(`Error executing change set ${changeSet.id}:`, error);
        setExecutingStackIds(null);
      }
    }

    setDialogOpen(false);
  };

  const checkStackStatus = (stackName: string) => {
    const intervalId = setInterval(async () => {
      try {
        const response = await cloudformation.describeStacks({
          StackName: stackName,
        });
        const status = response.Stacks?.[0]?.StackStatus;

        if (status) {
          console.log(`Stack ${stackName} execution completed with status: ${status}`);
          const updateStatus = (row: ChangeSet): ChangeSet => (row.name === stackName ? { ...row, status } : row);
          setRows((prevRows) => prevRows.map(updateStatus));
        }

        if (status !== "UPDATE_IN_PROGRESS") {
          clearInterval(intervalId);
          setExecutingStackIds(null);
        }
      } catch (error) {
        console.error(`Error checking status for stack ${stackName}:`, error);
      }
    }, 5000);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const infrastructureRows: ChangeSet[] = await fetchChangesets(companies);
      setRows(infrastructureRows);
    } catch (error) {
      console.error("Error refreshing stacks or change sets:", error);
    } finally {
      setLoading(false);
    }
  };

  function visibleRows(): readonly any[] {
    return rows
      .filter((row) => row.name.toLowerCase().includes(filterText.toLowerCase()))
      .map((row) => ({
        ...row,
        isExecuting: executingStackIds?.includes(row.id),
      }));
  }

  return (
    <Box id="infrastructure-main-container" display="flex" flexDirection="column" width="100%" height="94vh">
      <SearchHeader
        title="Infrastructure"
        onSearchChange={(e) => setFilterText(e.target.value)}
        filterText={filterText}
        textFilterProps={["filterText"]}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={isExecuteButtonDisabled()}
          onClick={() => executeChangesets()}
        >
          Execute
        </Button>
        <IconButton style={{ width: "2rem" }} onClick={refreshData} color="primary">
          <RefreshIcon />
        </IconButton>
      </SearchHeader>
      <Box display="flex" flexDirection="column" style={{ margin: "0 1.5rem", flexGrow: 1 }}>
        <Box style={{ flex: 1, marginBottom: "1rem", overflow: "hidden" }}>
          <CustomDataGrid
            rows={visibleRows()}
            columns={changesetColumns}
            checkboxSelection
            hideHeaders={loading}
            loading={loading}
            defaultHeight="100%"
            disableColumnFilter
            disableColumnSelector
            disableRowSelectionOnClick
            onRowSelectionModelChange={setSelectedRows}
            onRowClick={fetchChangeSetDetails}
            columnVisibilityModel={{
              __check__: true,
            }}
            dataCy="settings-infrastructure-table"
            initialState={{
              sorting: {
                sortModel: [{ field: "name", sort: "asc" }],
              },
            }}
          />
        </Box>
        <Box style={{ flex: 1, overflow: "hidden" }}>
          <Typography variant="h6">
            Changes ({changeSetDetails?.length ?? 0}) - {clickedChangeSetName}
          </Typography>
          <ChangesetDetails data={changeSetDetails || []} loading={loadingDetails} />
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Execution</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to execute the selected change sets?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmExecution} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Infrastructure;
